import React, { useState, useEffect, useCallback } from 'react';
import { Download, Eye, Loader, X, FileText, Users, Mail, TrendingUp, CheckCircle, Inbox } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { API, authAxios, downloadCv } from './adminApi';

const fmtSize = (b) => {
  if (!b) return '—';
  return b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};

const KPI_STYLE = { padding: '1rem 1.25rem', flex: '1 1 150px', minWidth: '150px' };

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <div className="card" style={KPI_STYLE} data-testid={`kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: accent || 'var(--brand-purple)' }}>
      <Icon size={16} />
      <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 800, marginTop: '6px' }}>{value}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = { parsed: '#0B815A', parsing: '#F59E0B', uploaded: '#6B7280', failed: '#EF4444' };
  const color = map[status] || '#6B7280';
  return (
    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '2px', textTransform: 'uppercase', color, background: `${color}15`, border: `1px solid ${color}35` }}>
      {status}
    </span>
  );
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.map(fmtVal).join('; ') || '—';
  if (typeof v === 'object') {
    return Object.entries(v)
      .filter(([, val]) => val !== null && val !== undefined && val !== '')
      .map(([k, val]) => `${k.replace(/_/g, ' ')}: ${fmtVal(val)}`)
      .join(', ') || '—';
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const ParsedModal = ({ data, onClose }) => (
  <div
    onClick={onClose}
    style={{ position: 'fixed', inset: 0, background: 'rgba(48,43,44,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="card"
      data-testid="parsed-cv-modal"
      style={{ maxWidth: '640px', width: '100%', maxHeight: '80vh', overflowY: 'auto', padding: '1.5rem', background: '#fff' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', margin: 0 }}>{data.filename}</h3>
        <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="close-parsed-modal"><X size={16} /></button>
      </div>
      {['parsed_profile', 'questionnaire_answers'].map(section => {
        const obj = data[section] || {};
        const entries = Object.entries(obj);
        return (
          <div key={section} style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
              {section.replace(/_/g, ' ')}
            </div>
            {entries.length === 0 ? (
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No data</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                {entries.map(([k, v]) => (
                  <div key={k} style={{ padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: '4px', fontSize: '0.8125rem', wordBreak: 'break-word' }}>
                    <span style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}: </span>
                    <strong>{fmtVal(v)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export const ReportingTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parsedView, setParsedView] = useState(null);
  const [parsedLoading, setParsedLoading] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const fetchReporting = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/admin/reporting`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load reporting data', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReporting(); }, [fetchReporting]);

  const viewParsed = async (uploadId) => {
    setParsedLoading(uploadId);
    try {
      const res = await authAxios.get(`${API}/admin/uploads/${uploadId}/parsed`);
      setParsedView(res.data);
    } finally {
      setParsedLoading(null);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader size={32} color="#F67425" className="animate-spin" /></div>;
  }

  if (loadError) {
    return (
      <div data-testid="reporting-error" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
        <p style={{ marginBottom: '1rem' }}>Could not load reporting data.</p>
        <button className="btn btn-ghost btn-sm" onClick={() => { setLoadError(false); setLoading(true); fetchReporting(); }}>Retry</button>
      </div>
    );
  }

  const t = data?.totals || {};
  const timeline = data?.timeline || [];
  const cvs = data?.cvs || [];

  return (
    <div data-testid="reporting-tab">
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <KpiCard icon={Users} label="Signups" value={t.total_users ?? 0} />
        <KpiCard icon={FileText} label="CVs Uploaded" value={t.total_uploads ?? 0} />
        <KpiCard icon={CheckCircle} label="CVs Parsed" value={t.parsed_cvs ?? 0} accent="#0B815A" />
        <KpiCard icon={TrendingUp} label="Assessments" value={t.total_assessments ?? 0} />
        <KpiCard icon={Inbox} label="Exit Leads" value={t.exit_leads ?? 0} accent="#BB0518" />
        <KpiCard icon={Mail} label="Report Requests" value={t.report_requests ?? 0} accent="#F59E0B" />
      </div>

      <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }} data-testid="reporting-chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', margin: 0 }}>Activity — Last 30 Days</h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Conversion: {t.conversion_rate ?? 0}% signups → assessments · {t.contacted ?? 0} contacted
          </span>
        </div>
        <div style={{ width: '100%', height: 260, minHeight: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={timeline} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gSignups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F67425" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#F67425" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gUploads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#302B2C" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#302B2C" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#BB0518" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#BB0518" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(48,43,44,0.08)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#8a8384' }} interval={4} />
              <YAxis tick={{ fontSize: 11, fill: '#8a8384' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: '4px', fontSize: '0.8125rem', border: '1px solid rgba(48,43,44,0.12)' }} />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              <Area type="monotone" dataKey="signups" name="Signups" stroke="#F67425" fill="url(#gSignups)" strokeWidth={2} />
              <Area type="monotone" dataKey="cv_uploads" name="CV Uploads" stroke="#302B2C" fill="url(#gUploads)" strokeWidth={2} />
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#BB0518" fill="url(#gLeads)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card" style={{ padding: '1.25rem' }} data-testid="cv-inventory-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', margin: 0 }}>CV Inventory ({cvs.length})</h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Check parsed data & download original files</span>
        </div>
        {cvs.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem 0' }}>No CVs uploaded yet</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  <th style={{ padding: '8px 10px' }}>Candidate</th>
                  <th style={{ padding: '8px 10px' }}>File</th>
                  <th style={{ padding: '8px 10px' }}>Size</th>
                  <th style={{ padding: '8px 10px' }}>Status</th>
                  <th style={{ padding: '8px 10px' }}>Uploaded</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cvs.map(cv => (
                  <tr key={cv.upload_id} data-testid={`cv-row-${cv.upload_id}`} style={{ borderTop: '1px solid var(--border-default)' }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 600 }}>{cv.user_name}</div>
                      {cv.user_email && <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{cv.user_email}</div>}
                    </td>
                    <td style={{ padding: '10px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cv.filename}>{cv.filename}</td>
                    <td style={{ padding: '10px', color: 'var(--text-muted)' }}>{fmtSize(cv.file_size)}</td>
                    <td style={{ padding: '10px' }}><StatusBadge status={cv.status} /></td>
                    <td style={{ padding: '10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{cv.uploaded_at ? new Date(cv.uploaded_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        data-testid={`check-cv-${cv.upload_id}`}
                        onClick={() => viewParsed(cv.upload_id)}
                        className="btn btn-ghost btn-sm"
                        disabled={parsedLoading === cv.upload_id}
                        style={{ fontSize: '0.75rem', marginRight: '4px' }}
                      >
                        {parsedLoading === cv.upload_id ? <Loader size={13} className="animate-spin" /> : <Eye size={13} />} Check
                      </button>
                      <button
                        data-testid={`download-cv-report-${cv.upload_id}`}
                        onClick={() => downloadCv(cv.upload_id, cv.filename)}
                        className="btn btn-ghost btn-sm"
                        disabled={!cv.has_file}
                        style={{ fontSize: '0.75rem', opacity: cv.has_file ? 1 : 0.4 }}
                      >
                        <Download size={13} /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {parsedView && <ParsedModal data={parsedView} onClose={() => setParsedView(null)} />}
    </div>
  );
};
